import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiInbox,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import Notification from "../../components/common/notification/Notification";
import Spinner from "../../components/common/spinner/Spinner";
import { usePostApiClient } from "./services/usePostApiClient";
import { createPostService } from "./services/postService";
import { POST_TABS, POST_DEFAULT_PAGE_SIZE } from "./constants/postEndpoints";
import PostPageHeader from "./components/PostPageHeader";
import PostFilterToolbar from "./components/PostFilterToolbar";
import PostCard from "./components/PostCard";
import PostTableView from "./components/PostTableView";
import PostDeleteModal from "./components/PostDeleteModal";
import usePersistentState from "../../hooks/usePersistentState";
import "./PostsPage.css";

// mode="mine" renders the My Space view: only the logged-in user's posts, no tabs
const PostsPage = ({ routeBase = "/customer/posts", mode = "all" }) => {
  const isMine = mode === "mine";
  const navigate = useNavigate();

  const authUser = useSelector((state) => state.auth?.user);
  const currentUserId =
    authUser?.user?.id || authUser?.id || authUser?.userDetails?.id || null;

  const userType =
    authUser?.user?.userType?.name ||
    authUser?.user?.userType ||
    authUser?.userType ||
    "CUSTOMER";
  const isPartner = String(userType).toUpperCase().includes("PARTNER");
  const isAdmin =
    String(userType).toUpperCase().includes("ADMIN") ||
    String(userType).toUpperCase().includes("ALL_HOME_DESIRE");

  const apiClient = usePostApiClient();
  const postService = useMemo(() => createPostService(apiClient), [apiClient]);

  // Active Tab
  const [storedTab, setActiveTab] = usePersistentState(
    `list:${routeBase}:tab`,
    POST_TABS.ALL
  );
  // Stale/unknown stored values (e.g. the removed "my" tab) fall back to All Posts
  const activeTab = Object.values(POST_TABS).includes(storedTab)
    ? storedTab
    : POST_TABS.ALL;

  // List data state
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filters, tab, view and page are remembered per list so back/forward returns to the same view
  const storageKey = `list:${routeBase}`;
  const [currentPage, setCurrentPage] = usePersistentState(`${storageKey}:page`, 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [error, setError] = useState(null);

  // Filters state
  const [searchTerm, setSearchTerm] = usePersistentState(`${storageKey}:search`, "");
  const [selectedService, setSelectedService] = usePersistentState(`${storageKey}:service`, "");
  const [selectedType, setSelectedType] = usePersistentState(`${storageKey}:type`, "");
  const [selectedState, setSelectedState] = usePersistentState(`${storageKey}:state`, "");
  const [selectedCity, setSelectedCity] = usePersistentState(`${storageKey}:city`, "");

  // Metadata for dropdowns
  const [servicesList, setServicesList] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // View mode
  const [viewMode, setViewMode] = usePersistentState(`${storageKey}:view`, "grid");

  // Modals state
  const [postToDelete, setPostToDelete] = useState(null);

  // Load initial metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [typesData, statesData, servicesData] = await Promise.all([
          postService.fetchPropertyTypes(),
          postService.fetchStates(1),
          postService.fetchServices(),
        ]);
        setPropertyTypes(typesData || []);
        setStates(statesData || []);
        setServicesList(servicesData || []);
      } catch (err) {
        console.error("Failed to load metadata:", err);
      }
    };
    fetchMetadata();
  }, [postService]);

  // A restored state filter needs its city options back
  useEffect(() => {
    if (!selectedState || states.length === 0) return;
    const stObj = states.find((st) => st.name === selectedState);
    if (stObj?.id) {
      postService.fetchCities(stObj.id).then((list) => setCities(list || []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states, postService]);

  // State selection handler
  const handleStateFilterChange = async (stateName) => {
    setSelectedState(stateName);
    setSelectedCity("");
    if (!stateName) {
      setCities([]);
      return;
    }
    const stObj = states.find((s) => s.name === stateName);
    if (stObj?.id) {
      try {
        const citiesData = await postService.fetchCities(stObj.id);
        setCities(citiesData || []);
      } catch (err) {
        console.error("Failed to load cities for state:", err);
      }
    }
  };

  // Main data fetching logic
  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let data = null;

      if (isMine) {
        // Same endpoint as the mobile app; owner is resolved server-side from the token
        data = await postService.listMySpace({ order_by: "DESC" });
      } else if (
        selectedService ||
        selectedType ||
        selectedState ||
        selectedCity ||
        searchTerm
      ) {
        data = await postService.listPreferred({
          service: selectedService,
          property_type: selectedType,
          state: selectedState,
          city: selectedCity,
          keyword_search: searchTerm,
          page_number: currentPage,
          page_size: POST_DEFAULT_PAGE_SIZE,
          order_by: "DESC",
          is_partner: true,
        });
      } else {
        data = await postService.listGeneral({
          page_number: currentPage,
          page_size: POST_DEFAULT_PAGE_SIZE,
          order_by: "DESC",
        });
      }

      let list =
        data?.leads ||
        data?.postAssets ||
        data?.data ||
        data?.posts ||
        (Array.isArray(data) ? data : []);

      // Client-side fallback filtering if backend preferred endpoint returns full list
      if (searchTerm || selectedService || selectedType || selectedState || selectedCity) {
        list = list.filter((p) => {
          const matchSearch =
            !searchTerm ||
            (p.service && p.service.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.city_name && p.city_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.state_name && p.state_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.customer?.first_name && p.customer.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (p.partner?.first_name && p.partner.first_name.toLowerCase().includes(searchTerm.toLowerCase()));

          const matchService = !selectedService || p.service === selectedService;
          const propTypeName = typeof p.property_type === "object" ? p.property_type?.name : p.property_type;
          const matchType = !selectedType || propTypeName === selectedType;
          const matchState = !selectedState || p.state_name === selectedState;
          const matchCity = !selectedCity || p.city_name === selectedCity;

          return matchSearch && matchService && matchType && matchState && matchCity;
        });
      }

      if (isMine) {
        // My Space endpoint is not paginated server-side, so page locally
        const unfilteredCount = (data?.leads || []).length;
        setTotalPages(Math.max(1, Math.ceil(list.length / POST_DEFAULT_PAGE_SIZE)));
        setPosts(
          list.slice(
            (currentPage - 1) * POST_DEFAULT_PAGE_SIZE,
            currentPage * POST_DEFAULT_PAGE_SIZE
          )
        );
        setTotalCount(unfilteredCount);
        return;
      }

      setPosts(list);
      setTotalPages(data?.totalPages || data?.totalPage || data?.total_pages || 1);
      if (typeof data?.totalCount === "number") {
        setTotalCount(data.totalCount);
      } else if (typeof data?.totalRecord === "number") {
        setTotalCount(data.totalRecord);
      } else {
        setTotalCount(list.length);
      }
    } catch (error) {
      console.warn("Error fetching posts:", error);
      setError("Unable to load posts. Please check your network connection.");
      setPosts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    isMine,
    currentPage,
    searchTerm,
    selectedCity,
    selectedService,
    selectedState,
    selectedType,
    postService,
  ]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Tab change
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1);
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedService("");
    setSelectedType("");
    setSelectedState("");
    setSelectedCity("");
    setCurrentPage(1);
  };

  // Dedicated page navigation handlers (NO MODAL)
  const normalizedRouteBase = routeBase.replace(/\/+$/, "");

  const handleViewDetails = (post) => {
    navigate(`${normalizedRouteBase}/${post.id}`);
  };

  const handleOpenComments = (post) => {
    navigate(`${normalizedRouteBase}/${post.id}/discussion`);
  };

  const handleDeletePrompt = (post) => {
    setPostToDelete(post);
  };

  const handlePostDeleted = (deletedId) => {
    setPosts((prev) => prev.filter((p) => p.id !== deletedId));
  };

  const canCreate = true;

  return (
    <div className="posts-module-page">
      <Notification />

      {/* Page Header */}
      <PostPageHeader
        title={isMine ? "My Posts" : "Shared Posts"}
        description={
          isMine
            ? "Posts you have published. Only posts created by your account appear here."
            : "Browse community requests, service inquiries, customer requirements, and connect with potential clients and partners."
        }
        totalCount={totalCount}
        canCreate={canCreate}
        createLink="/post/add-post"
      />

      {/* Filter Toolbar */}
      <PostFilterToolbar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onSearchSubmit={fetchPosts}
        selectedService={selectedService}
        onServiceChange={(val) => {
          setSelectedService(val);
          setCurrentPage(1);
        }}
        services={servicesList}
        selectedType={selectedType}
        onTypeChange={(val) => {
          setSelectedType(val);
          setCurrentPage(1);
        }}
        propertyTypes={propertyTypes}
        selectedState={selectedState}
        onStateChange={handleStateFilterChange}
        states={states}
        selectedCity={selectedCity}
        onCityChange={(val) => {
          setSelectedCity(val);
          setCurrentPage(1);
        }}
        cities={cities}
        onResetFilters={handleResetFilters}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        showTabs={!isMine}
      />

      {/* Main Content Area */}
      {loading ? (
        <div className="posts-loading-wrap">
          <Spinner loading={true} />
          <p className="mt-3 text-muted">Loading posts...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex align-items-center justify-content-between p-3 rounded-3 my-4" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={fetchPosts}>
            Retry
          </button>
        </div>
      ) : posts.length === 0 ? (
        <div className="posts-empty-state">
          <div className="posts-empty-state__icon">
            <FiInbox size={48} />
          </div>
          <h3 className="posts-empty-state__title">
            {isMine && !(searchTerm || selectedService || selectedType || selectedState || selectedCity)
              ? "You haven't created any posts yet"
              : "No Posts Found"}
          </h3>
          <p className="posts-empty-state__text">
            {searchTerm || selectedService || selectedType || selectedState || selectedCity
              ? "Try adjusting your search criteria or reset filters to see all available posts."
              : isMine
              ? "You haven't published any requirement posts yet. Click 'Create Post' to share your first requirement."
              : "There are currently no active posts listed in this category."}
          </p>
          {(searchTerm || selectedService || selectedType || selectedState || selectedCity) && (
            <button
              type="button"
              className="btn btn-outline-primary mt-2"
              onClick={handleResetFilters}
            >
              Reset All Filters
            </button>
          )}
          {isMine && !(searchTerm || selectedService || selectedType || selectedState || selectedCity) && (
            <button
              type="button"
              className="btn btn-primary mt-2"
              onClick={() => navigate("/post/add-post")}
            >
              Create Post
            </button>
          )}
        </div>
      ) : viewMode === "grid" ? (
        <div className="posts-grid">
          {posts.map((post) => {
            const isOwner =
              currentUserId &&
              Number(post.customer?.id || post.partner?.id || post.user?.id) ===
                Number(currentUserId);
            const canDelete = isOwner || isAdmin;

            return (
              <PostCard
                key={post.id}
                post={post}
                onViewDetails={handleViewDetails}
                onOpenComments={handleOpenComments}
                onDelete={handleDeletePrompt}
                canDelete={canDelete}
              />
            );
          })}
        </div>
      ) : (
        <PostTableView
          posts={posts}
          onViewDetails={handleViewDetails}
          onOpenComments={handleOpenComments}
          onDelete={handleDeletePrompt}
          canDelete={isAdmin || isPartner}
        />
      )}

      {/* Pagination Bar */}
      {totalPages > 1 && !loading && (
        <div className="posts-pagination-bar">
          <button
            type="button"
            className="posts-page-nav-btn"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          >
            <FiChevronLeft size={16} />
            <span>Previous</span>
          </button>

          <span className="posts-page-counter">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>

          <button
            type="button"
            className="posts-page-nav-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          >
            <span>Next</span>
            <FiChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <PostDeleteModal
        show={Boolean(postToDelete)}
        onHide={() => setPostToDelete(null)}
        post={postToDelete}
        postService={postService}
        onPostDeleted={handlePostDeleted}
      />
    </div>
  );
};

export default PostsPage;
